// SPA router (stage 5: username+password app wall).
//
//  /  → Login (first screen). admin creds → /admin, user creds → /app.
//  /app     → ColorSep editor (persists server-side state while hidden).
//  /account → user Account page.
//  /admin   → admin panel (sidebar layout), admin-role session required.
//
// The email-based signup/verify/reset/pricing flow was REMOVED by decision;
// accounts are provisioned by the admin (Users page / server store).

import { BrowserRouter, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { RequireAuth, RequireAdmin } from './auth/RequireAuth';
import { ToastHost } from './ui/Toast';
import { LicenseProvider, useLicense } from './license/LicenseProvider';
import { LicenseBanner, gateFor } from './license/LicenseGate';
import App from './App';
import { LoginPage } from './pages/Login';
import { AccountPage } from './pages/Account';
import { AdminPage } from './pages/Admin';
import './ui/tokens.css';
import './ui/screens.css';
import './index.css';

/**
 * Always keep the editor mounted; hide it (visibility, not display) so
 * canvas layout does not thrash. Other routes overlay it.
 */
function EditorShell() {
  const location = useLocation();
  const editorVisible = location.pathname.startsWith('/app');
  return (
    <div
      className="editor-persistent"
      style={{ visibility: editorVisible ? 'visible' : 'hidden', height: editorVisible ? undefined : 0 }}
      aria-hidden={!editorVisible}
    >
      <EditorGate paused={!editorVisible} />
    </div>
  );
}

/**
 * Editor gate that does NOT navigate: the landing logic lives on the
 * editor route (/app → RequireAuth → Navigate /login). Navigating from a
 * shell gate is a loop hazard on pages that render the shell themselves
 * (e.g. /login renders the editor shell in the background).
 */
function EditorGate({ paused }: { paused: boolean }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'transparent' }}>
        <div className="skeleton" style={{ width: 240, height: 16 }} />
      </div>
    );
  }
  if (!user) return null;
  return <EditorWithGate paused={paused} />;
}

/** LicenseProvider context → App exportGate + banner under the toolbar. */
function EditorWithGate({ paused }: { paused: boolean }) {
  const state = useLicense();
  const gate = gateFor(state);
  return <App paused={paused} exportGate={gate} banner={<LicenseBanner />} />;
}

function AccountGate() {
  return (
    <RequireAuth>
      <AccountPage />
    </RequireAuth>
  );
}

function AdminGate() {
  return <RequireAdmin><AdminPage /></RequireAdmin>;
}

function AppRoutes() {
  return (
    <>
      {/* persistent editor shell: mounted on every route */}
      <div className="editor-host">
        <EditorShell />
      </div>
      {/* page overlays (login etc.) cover the editor when active */}
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/app/*" element={null} />
        <Route path="/account" element={<AccountGate />} />
        <Route path="/admin" element={<AdminGate />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export function Root() {
  return (
    <AuthProvider>
      <LicenseProvider>
        <ToastHost>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ToastHost>
      </LicenseProvider>
    </AuthProvider>
  );
}
